import { useEffect, useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MoviesContext } from "@context/moviesContext";
import { useTranslation } from "react-i18next";

import {
  selectCtrl,
  selectIsOpenMainSidebar,
  setIsOpenMainSidebar,
  setSelectedType,
} from "@app/global";
import SvgSearch from "@assets/icons/SvgSearch";
import SvgGenres from "@assets/icons/SvgGenres";
import SvgLogout from "@assets/icons/SvgLogout";

import "@styles/components/mainSidebar.scss";
import useKeydown from "@hooks/useKeydown";
import {
  setCtrl,
  setIsMovieSearchBarOpen,
  selectSelectedType,
} from "@app/global";
import { signOut } from "@server/requests";
import LOCAL_STORAGE from "@utils/localStorage";

const MainSidebar = ({ categories }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { menuList } = useContext(MoviesContext);

  const isOpen = useSelector(selectIsOpenMainSidebar);
  const ctrl = useSelector(selectCtrl);
  const selectedType = useSelector(selectSelectedType);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [active, setActive] = useState(0);
  const [isCategoriesOpened, setIsCategoriesOpened] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLauncherMode, setIsLauncherMode] = useState(true);

  // Check if launcher mode is disabled
  useEffect(() => {
    const appSettings = localStorage.getItem("app_settings");
    if (appSettings) {
      const settings = JSON.parse(appSettings);
      setIsLauncherMode(settings.launcher !== false);
    }
  }, []);

  const items = [
    {
      name: t("Search"),
      icon: <SvgSearch />,
    },
  ];

  const handleLogout = async () => {
    try {
      const token = LOCAL_STORAGE.TOKEN.GET();
      if (token) {
        await signOut({ token });
      }
    } catch (error) {
      console.log("Logout error:", error);
    } finally {
      // Clear local storage and redirect regardless of API response
      LOCAL_STORAGE.TOKEN.REMOVE();
      LOCAL_STORAGE.LAST_CHANNEL_ID.REMOVE();
      localStorage.removeItem("parental_code");
      navigate("/");
    }
  };

  useEffect(() => {
    const supportedTypes = ["movies", "tv-shows", "favorites"];
    if (categories && menuList) {
      const newItems = [
        ...items,
        ...menuList
          .filter((item) => supportedTypes.includes(item.type))
          .map((item) => ({
            name: t(item.name),
            id: item.id,
            selectedIcon: item.selectedIcon,
            type:
              item.type === "movies"
                ? "movie"
                : item.type === "tv-shows"
                  ? "tv_show"
                  : item.type,
            icon: item.icon ? (
              <img
                src={item.icon}
                alt={item.name}
                className="main-sidebar-item_icon"
              />
            ) : (
              <SvgGenres />
            ),
          })),
      ];

      // Add logout button if not in launcher mode
      if (!isLauncherMode) {
        newItems.push({
          name: t("Logout"),
          icon: <SvgLogout />,
          type: "logout",
          isLogout: true,
        });
      }

      setSidebarItems(newItems);
    }
  }, [categories, menuList, isLauncherMode, t]);

  useEffect(() => {
    if (sidebarItems.length > 0) {
      const foundMovieType = sidebarItems.find((item) => item.type === "movie");

      if (foundMovieType) {
        // setSelectedType(foundMovieType.type);
      } else {
        // setSelectedType(sidebarItems[1].type);
      }
    }
  }, [sidebarItems]);

  useKeydown({
    isActive: isOpen && ctrl === "mainSidebar",

    right: () => {
      dispatch(setIsOpenMainSidebar(false));
      dispatch(setCtrl("moviesSeries"));
    },

    up: () => {
      if (active > 0) {
        setActive(active - 1);
        if (active < 6) {
          setTranslateY((prev) => Math.min(prev + 8, 0));
        }
      }
    },
    down: () => {
      if (active < sidebarItems.length - 1) {
        setActive(active + 1);
        if (active > 2) {
          setTranslateY((prev) =>
            Math.max(prev - 8, -((sidebarItems.length - 1) * 8) + 6)
          );
        }
      }
    },

    ok: () => {
      const selectedItem = sidebarItems[active];

      if (active === 0) {
        dispatch(setCtrl("inp"));
        dispatch(setIsOpenMainSidebar(false));
        dispatch(setIsMovieSearchBarOpen(true));
      } else if (selectedItem?.isLogout) {
        handleLogout();
      } else {
        handleCategorySelect(sidebarItems[active].type);
        dispatch(setIsOpenMainSidebar(false));
      }
    },

    back: () => {
      dispatch(setIsOpenMainSidebar(false));
      dispatch(setCtrl("moviesSeries"));
      setIsCategoriesOpened(false);
    },
  });

  const handleCategorySelect = (type) => {
    // setSelectedType(type);
    dispatch(setSelectedType(type));
    setIsCategoriesOpened(false);
    dispatch(setIsOpenMainSidebar(false));
    dispatch(setCtrl("moviesSeries"));
  };

  useEffect(() => {
    setSelectedItem(selectedType);
  }, [selectedType]);

  return (
    <div className={`main-sidebar ${isOpen ? "open" : ""}`}>
      <div className="outline"></div>

      <div
        className={`sidebar-items_wrapper ${sidebarItems.length > 3 ? "hidden" : ""}`}
      >
        <div
          className={`items ${isCategoriesOpened ? "categories-opened" : ""}`}
          style={{ transform: `translateY(${translateY}rem)` }}
        >
          {sidebarItems.map((item, index) => {
            return (
              <div
                className={`item ${active === index ? "active" : ""} ${item.items ? "parent" : ""} ${item.type === selectedItem ? "selected" : ""} ${item.isLogout ? "logout-item" : ""}`}
                key={index}
              >
                <div className="icon">
                  {active === index && item.selectedIcon ? (
                    <img
                      src={item.selectedIcon}
                      alt={item.name}
                      className="selected-icon"
                    />
                  ) : (
                    item.icon
                  )}
                </div>
                <p className="text">{item.name}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CategoriesList = ({
  categories,
  isOpen,
  setIsOpen,
  ctrl,
  onSelectCategory,
  selectedIcon,
}) => {
  const dispatch = useDispatch();

  const [catActive, setCatActive] = useState(0);

  const [translateY, setTranslateY] = useState(0);

  useKeydown({
    isActive: isOpen && ctrl === "movieCategories",

    up: () => {
      if (catActive > 0) {
        setCatActive(catActive - 1);
        setTranslateY((prev) => Math.min(prev + 2.8, 0)); // Limit upward translation
      } else {
        dispatch(setCtrl("mainSidebar"));
        setIsOpen(false);
      }
    },
    down: () => {
      if (catActive < categories.length - 1) {
        setCatActive(catActive + 1);
        if (catActive >= 3) {
          setTranslateY((prev) =>
            Math.max(prev - 2.8, -((categories.length - 1) * 2.8) + 6)
          );
        }
      }
    },

    ok: () => {
      onSelectCategory(categories[catActive].id);
    },

    back: () => {
      dispatch(setCtrl("mainSidebar"));
      setIsOpen(false);
    },
  });

  return (
    <div className="categories">
      <div
        className="categories-list_wrapper"
        style={{ transform: `translateY(${translateY}rem)` }}
      >
        {!categories.length ? (
          <p>No Categories</p>
        ) : (
          <>
            {categories.map((category, idx) => {
              return (
                <p
                  key={idx}
                  className={`name ${idx === catActive ? "active" : ""}`}
                >
                  {category.name}
                </p>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default MainSidebar;
